import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Alert from '../common/Alert.js';
import axiosInstance from '../../config/axios.js';
import { Calendar, ArrowLeft } from 'lucide-react';
import {
  PageLayout,
  Button,
  DetailCard,
  DetailRow,
  StatusBadge,
} from '../common/index.js';

const ViewAttendance = () => {
  const { attendanceId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [attendance, setAttendance] = useState(null);

  useEffect(() => {
    fetchAttendance();
  }, [attendanceId]);

  const fetchAttendance = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/attendance/${attendanceId}`);
      setAttendance(response.data.data);
    } catch (err) {
      Alert.error('Failed to load attendance details');
      console.error(err);
      navigate('/attendance');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  const renderLocation = (location) => {
    if (!location) return 'N/A';
    return (
      <div className="space-y-1 text-sm">
        <div>Village: {location.village}</div>
        <div>City: {location.city}</div>
        <div>District: {location.district}</div>
        <div>State: {location.state}</div>
        <div>Country: {location.country}</div>
        <div className="text-xs text-gray-500 font-mono pt-1">
          {location.latitude}, {location.longitude}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <PageLayout title="Attendance Details" icon={Calendar}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4" />
            <p className="text-gray-600">Loading attendance details...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!attendance) return null;

  return (
    <PageLayout
      title="Attendance Details"
      icon={Calendar}
      actions={
        <Button variant="outline" icon={ArrowLeft} onClick={() => navigate('/attendance')}>
          Back to List
        </Button>
      }
    >
      <div className={`rounded-card p-4 mb-4 border ${attendance.is_logged_out ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="font-semibold text-gray-900">
              {attendance.is_logged_out ? 'Completed Session' : 'Active Session'}
            </p>
            <p className="text-sm text-gray-600">
              {attendance.is_logged_out
                ? `Total work duration: ${attendance.work_hours_formatted}`
                : 'Session is currently ongoing'}
            </p>
          </div>
          <StatusBadge status={attendance.is_logged_out ? 'completed' : 'active'} />
        </div>
      </div>

      <div className="space-y-4">
        <DetailCard title="User Information">
          <DetailRow label="User Name" value={attendance.user_name} />
          <DetailRow label="Date" value={formatDate(attendance.attendance_date)} />
        </DetailCard>

        <DetailCard title="Time Information">
          <DetailRow label="Login Time" value={formatTime(attendance.login_time)} />
          <DetailRow
            label="Logout Time"
            value={attendance.is_logged_out ? formatTime(attendance.logout_time) : 'Still Active'}
          />
          <DetailRow label="Total Work Duration" value={attendance.work_hours_formatted} />
        </DetailCard>

        <DetailCard title="Location Information">
          <DetailRow label="Login Location" value={renderLocation(attendance.login_location)} />
          <DetailRow
            label="Logout Location"
            value={
              attendance.logout_location
                ? renderLocation(attendance.logout_location)
                : attendance.is_logged_out
                  ? 'No location data available'
                  : 'User has not logged out yet'
            }
          />
        </DetailCard>
      </div>
    </PageLayout>
  );
};

export default ViewAttendance;
